import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { useHomeStats } from '../hooks/useHomeStats';
import { formatAmount } from '../../../utils/numberFormat';
import { BrandRule } from './_shared';

// The keys are kept for i18n stability; the copy now says only what the site
// does today: licence numbers shown when the listing has them (`verified`),
// and describe-it / draw-it search (`faster`).
const FEATURES = [
  { key: 'verified', icon: 'ri-file-shield-2-line' },
  { key: 'support', icon: 'ri-customer-service-2-line' },
  { key: 'faster', icon: 'ri-chat-search-line' },
] as const;

// "The trusted place to move in Saudi Arabia", set as a SPEC SHEET (2026-10-06).
//
// Every listing on the site carries a specification table; this band uses the
// same form for the platform's own guarantees, so the promise reads like the
// thing it is promising. It replaced three icon-tile features over a row of
// giant numbers with tiny labels. The live numbers now sit inside one sentence
// at reading size, and each label agrees with its number in Arabic
// (6 → «مدن», 120 → «وسيطاً», 100 → «وسيط») through i18next's plural keys.
// Any stat we can't back with data is omitted rather than invented.
export default function WhyUs() {
  const { t, i18n } = useTranslation(['home']);
  const isRTL = i18n.dir() === 'rtl';
  const arrow = isRTL ? 'ri-arrow-left-line' : 'ri-arrow-right-line';
  const { user } = useAuth();
  const { listings, brokers, cities } = useHomeStats();

  const stats = [
    { key: 'listings', value: listings },
    { key: 'brokers', value: brokers },
    { key: 'cities', value: cities },
  ].filter((s): s is { key: string; value: number } => typeof s.value === 'number' && s.value > 0);

  return (
    <section className="bg-white pb-14 sm:pb-20">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-5">
          <h2 className="text-balance font-display text-2xl font-bold leading-tight text-[var(--pv-color-primary)] sm:text-3xl md:text-[2.4rem]">
            {t('home.v2.why.title')}
          </h2>
          <BrandRule className="mt-4" />

          {stats.length > 0 && (
            <p className="mt-6 text-base leading-loose text-[var(--pv-color-text-muted)]">
              {/* Each number+label pair is unbreakable; the separators sit
                  between them with real spaces, so the line can wrap there. */}
              {stats.map((s, i) => (
                <Fragment key={s.key}>
                  {i > 0 && (
                    <>
                      {' '}
                      <span aria-hidden="true" className="mx-1">·</span>{' '}
                    </>
                  )}
                  <span className="whitespace-nowrap">
                    <strong className="font-bold tabular-nums text-[var(--pv-color-primary)]">
                      {formatAmount(s.value, { maximumFractionDigits: 0 })}
                    </strong>{' '}
                    {t(`home.v2.why.stats.${s.key}`, { count: s.value })}
                  </span>
                </Fragment>
              ))}
            </p>
          )}

          {!user && (
            <Link
              to="/register"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#004D40] px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#003930] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            >
              {t('home.v2.why.cta')}
              <i className={`${arrow} transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5`} aria-hidden="true" />
            </Link>
          )}
        </div>

        {/* The spec sheet: label column + value column, hairlines between rows,
            exactly as a listing's specification table is set. */}
        <dl className="divide-y divide-[var(--pv-border-soft)] border-y border-[var(--pv-border-soft)] lg:col-span-7 lg:self-center">
          {FEATURES.map((f) => (
            <div key={f.key} className="grid gap-1.5 py-6 sm:grid-cols-[12rem_1fr] sm:gap-8">
              <dt className="flex items-center gap-2.5 text-base font-semibold text-[var(--pv-color-text)]">
                <i className={`${f.icon} text-lg text-[var(--pv-color-primary)]`} aria-hidden="true" />
                {t(`home.v2.why.items.${f.key}.title`)}
              </dt>
              <dd className="text-sm leading-relaxed text-[var(--pv-color-text-muted)]">
                {t(`home.v2.why.items.${f.key}.body`)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
